import {
  SIGNAL_VERSION,
  isForMe,
  restartOfferer,
  shouldOfferOnHello,
  type IceCandidateJson,
  type Signal,
  type SignalFrom,
} from './signalling';

/**
 * The call's peer connections (h1-plan.md section 2, "Mesh at 12"). No React:
 * useHuddle owns one of these per call and feeds it signals.
 *
 * - Audio only, one Opus track per connection, sender capped at 40 kbps.
 * - Existing members offer to a newcomer on its hello; the newcomer answers.
 * - A peer that has not connected 15 s after it appeared is 'failed' on its
 *   tile ("Can't connect"); after 5 s 'disconnected', the smaller sessionId
 *   offers with iceRestart.
 * - Messages from a session that is not in the roster are dropped (a filter,
 *   not authentication: h1-plan.md:185).
 */

export const MAX_BITRATE_BPS = 40_000;
export const CONNECT_TIMEOUT_MS = 15_000;
export const RESTART_AFTER_MS = 5_000;
/** STUN only: what a call uses when the relay could not be had (turn.ts). */
export const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

export type PeerStatus = 'joining' | 'connected' | 'failed';

export interface PeerView {
  sessionId: string;
  userId: string;
  name: string;
  status: PeerStatus;
  muted: boolean;
  speaking: boolean;
}

/** The slices of the browser APIs the mesh touches, so tests can fake them. */
export interface MeshEnv {
  createPeer: (config: RTCConfiguration) => RTCPeerConnection;
  createAudio: () => HTMLAudioElement;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface MeshOptions {
  me: SignalFrom;
  name: string;
  stream: MediaStream;
  send: (signal: Signal) => void;
  onChange: () => void;
  /** A hello from a seat we do not know yet: refresh the roster soon. */
  onUnknownSender?: () => void;
  /**
   * The call's ICE servers, fetched once before the first peer and shared by
   * every peer (turn.ts). Missing means STUN only.
   */
  iceServers?: RTCIceServer[];
  env: MeshEnv;
}

/** Hellos waiting for a roster refresh; more than this is not a real call. */
export const PARKED_MAX = 12;

interface Peer {
  view: PeerView;
  pc: RTCPeerConnection;
  audio: HTMLAudioElement;
  pendingIce: IceCandidateJson[];
  connectTimer: unknown;
  restartTimer: unknown;
}

export class HuddleMesh {
  private readonly opts: MeshOptions;
  private readonly peers = new Map<string, Peer>();
  private roster = new Map<string, { userId: string; name: string }>();
  private readonly parked = new Map<string, Signal>();
  private muted = false;
  private closed = false;
  private iceServers: RTCIceServer[];

  constructor(opts: MeshOptions) {
    this.opts = opts;
    this.iceServers = opts.iceServers ?? ICE_SERVERS;
  }

  /**
   * Fresh relay credentials (turn.ts refresh): new peers get them, and every
   * open peer is told with setConfiguration. A browser that refuses the
   * change keeps its old servers; the call goes on.
   */
  setIceServers(servers: RTCIceServer[]): void {
    if (this.closed) return;
    this.iceServers = servers;
    for (const peer of this.peers.values()) {
      try {
        peer.pc.setConfiguration({ ...peer.pc.getConfiguration(), iceServers: servers });
      } catch {
        // Keep the servers this peer already has.
      }
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Tiles, in a stable order. */
  views(): PeerView[] {
    return [...this.peers.values()]
      .map((p) => ({ ...p.view }))
      .sort((a, b) => a.name.localeCompare(b.name) || a.sessionId.localeCompare(b.sessionId));
  }

  /** The seats the database says are in the call. Peers that left it are closed. */
  setRoster(seats: { userId: string; sessionId: string; name: string }[]): void {
    this.roster = new Map(
      seats
        .filter((s) => s.sessionId !== this.opts.me.sessionId)
        .map((s) => [s.sessionId, { userId: s.userId, name: s.name }]),
    );
    for (const sessionId of [...this.peers.keys()]) {
      if (!this.roster.has(sessionId)) this.dropPeer(sessionId);
    }
    this.opts.onChange();
    const parked = [...this.parked.values()];
    this.parked.clear();
    for (const hello of parked) {
      const known = this.roster.get(hello.from.sessionId);
      if (known !== undefined && known.userId === hello.from.userId) void this.handle(hello);
    }
  }

  /** Said once after the channel is subscribed. */
  hello(): void {
    this.send({ v: SIGNAL_VERSION, type: 'hello', from: this.opts.me, name: this.opts.name });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    for (const track of this.opts.stream.getAudioTracks()) track.enabled = !muted;
    this.send({ v: SIGNAL_VERSION, type: 'mute', from: this.opts.me, muted });
  }

  /** Mark a tile as speaking (fed from an AnalyserNode by the hook). */
  setSpeaking(sessionId: string, speaking: boolean): void {
    const peer = this.peers.get(sessionId);
    if (peer === undefined || peer.view.speaking === speaking) return;
    peer.view.speaking = speaking;
    this.opts.onChange();
  }

  /** Play every remote element; called inside the Join click for Safari (h1-plan.md:242). */
  unlockAudio(): void {
    for (const peer of this.peers.values()) void peer.audio.play().catch(() => undefined);
  }

  async handle(signal: Signal): Promise<void> {
    if (this.closed) return;
    if (signal.from.sessionId === this.opts.me.sessionId) return;
    if (!isForMe(signal, this.opts.me.sessionId)) return;
    const known = this.roster.get(signal.from.sessionId);
    if (known === undefined || known.userId !== signal.from.userId) {
      // A newcomer's hello usually beats our next heartbeat, so our roster
      // does not have it yet. Park the hello (latest per session, a few at
      // most), ask for a fresh roster, and replay it only if the database
      // then lists that exact seat. Anything else from a stranger is dropped.
      if (signal.type === 'hello' && this.parked.size < PARKED_MAX) {
        this.parked.set(signal.from.sessionId, signal);
        this.opts.onUnknownSender?.();
      }
      return;
    }

    switch (signal.type) {
      case 'hello': {
        if (shouldOfferOnHello(true)) {
          this.dropPeer(signal.from.sessionId); // a reload arrives as a fresh hello
          const peer = this.ensurePeer(signal.from, known.name);
          await this.makeOffer(peer, false);
        }
        return;
      }
      case 'offer': {
        const peer = this.ensurePeer(signal.from, known.name);
        if (peer.pc.signalingState === 'have-local-offer') {
          // Glare: two people joined at once and both offered. The smaller
          // sessionId keeps its offer; the other rolls back and answers.
          if (restartOfferer(this.opts.me.sessionId, signal.from.sessionId)) return;
          await peer.pc.setLocalDescription({ type: 'rollback' });
        }
        await peer.pc.setRemoteDescription({ type: 'offer', sdp: signal.sdp });
        await this.flushIce(peer);
        const answer = await peer.pc.createAnswer();
        await peer.pc.setLocalDescription(answer);
        this.send({
          v: SIGNAL_VERSION,
          type: 'answer',
          from: this.opts.me,
          to: signal.from.sessionId,
          sdp: answer.sdp ?? '',
        });
        return;
      }
      case 'answer': {
        const peer = this.peers.get(signal.from.sessionId);
        if (peer === undefined || peer.pc.signalingState !== 'have-local-offer') return;
        await peer.pc.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
        await this.flushIce(peer);
        return;
      }
      case 'ice': {
        const peer = this.peers.get(signal.from.sessionId);
        if (peer === undefined || signal.candidate === null) return;
        if (peer.pc.remoteDescription === null) {
          peer.pendingIce.push(signal.candidate);
          return;
        }
        await peer.pc.addIceCandidate(signal.candidate).catch(() => undefined);
        return;
      }
      case 'mute': {
        const peer = this.peers.get(signal.from.sessionId);
        if (peer === undefined) return;
        peer.view.muted = signal.muted;
        this.opts.onChange();
        return;
      }
      case 'bye':
        this.dropPeer(signal.from.sessionId);
        this.opts.onChange();
        return;
      case 'roster':
        // The hook refetches the roster through the heartbeat.
        return;
    }
  }

  /** Leave: say bye, close everything. Safe to call twice. */
  close(sayBye = true): void {
    if (this.closed) return;
    if (sayBye) this.send({ v: SIGNAL_VERSION, type: 'bye', from: this.opts.me });
    this.closed = true;
    for (const sessionId of [...this.peers.keys()]) this.dropPeer(sessionId);
  }

  private send(signal: Signal): void {
    if (this.closed) return;
    this.opts.send(signal);
  }

  private ensurePeer(from: SignalFrom, name: string): Peer {
    const existing = this.peers.get(from.sessionId);
    if (existing !== undefined) return existing;
    const { env } = this.opts;
    const pc = env.createPeer({ iceServers: this.iceServers });
    const audio = env.createAudio();
    audio.autoplay = true;
    const peer: Peer = {
      view: {
        sessionId: from.sessionId,
        userId: from.userId,
        name,
        status: 'joining',
        muted: false,
        speaking: false,
      },
      pc,
      audio,
      pendingIce: [],
      connectTimer: null,
      restartTimer: null,
    };
    this.peers.set(from.sessionId, peer);

    for (const track of this.opts.stream.getAudioTracks()) {
      const sender = pc.addTrack(track, this.opts.stream);
      void capBitrate(sender);
    }
    pc.ontrack = (event) => {
      const [remote] = event.streams;
      if (remote !== undefined) audio.srcObject = remote;
      void audio.play().catch(() => undefined);
    };
    pc.onicecandidate = (event) => {
      this.send({
        v: SIGNAL_VERSION,
        type: 'ice',
        from: this.opts.me,
        to: from.sessionId,
        candidate: event.candidate === null ? null : toCandidateJson(event.candidate),
      });
    };
    pc.oniceconnectionstatechange = () => {
      this.onIceState(peer);
    };
    peer.connectTimer = env.setTimeout(() => {
      if (peer.view.status === 'joining') {
        peer.view.status = 'failed';
        this.opts.onChange();
      }
    }, CONNECT_TIMEOUT_MS);
    this.opts.onChange();
    return peer;
  }

  private onIceState(peer: Peer): void {
    const state = peer.pc.iceConnectionState;
    const { env } = this.opts;
    if (state === 'connected' || state === 'completed') {
      env.clearTimeout(peer.connectTimer);
      env.clearTimeout(peer.restartTimer);
      peer.restartTimer = null;
      if (peer.view.status !== 'connected') {
        peer.view.status = 'connected';
        this.opts.onChange();
      }
      return;
    }
    if (state === 'failed') {
      peer.view.status = 'failed';
      this.opts.onChange();
      return;
    }
    if (state === 'disconnected' && peer.restartTimer === null) {
      peer.restartTimer = env.setTimeout(() => {
        peer.restartTimer = null;
        if (
          peer.pc.iceConnectionState === 'disconnected' &&
          restartOfferer(this.opts.me.sessionId, peer.view.sessionId)
        ) {
          void this.makeOffer(peer, true);
        }
      }, RESTART_AFTER_MS);
    }
  }

  private async makeOffer(peer: Peer, iceRestart: boolean): Promise<void> {
    const offer = await peer.pc.createOffer(iceRestart ? { iceRestart: true } : {});
    await peer.pc.setLocalDescription(offer);
    this.send({
      v: SIGNAL_VERSION,
      type: 'offer',
      from: this.opts.me,
      to: peer.view.sessionId,
      sdp: offer.sdp ?? '',
    });
  }

  private async flushIce(peer: Peer): Promise<void> {
    const pending = peer.pendingIce.splice(0);
    for (const candidate of pending) {
      await peer.pc.addIceCandidate(candidate).catch(() => undefined);
    }
  }

  private dropPeer(sessionId: string): void {
    const peer = this.peers.get(sessionId);
    if (peer === undefined) return;
    this.peers.delete(sessionId);
    this.opts.env.clearTimeout(peer.connectTimer);
    this.opts.env.clearTimeout(peer.restartTimer);
    peer.pc.ontrack = null;
    peer.pc.onicecandidate = null;
    peer.pc.oniceconnectionstatechange = null;
    peer.pc.close();
    peer.audio.pause();
    peer.audio.srcObject = null;
  }
}

function toCandidateJson(candidate: RTCIceCandidate): IceCandidateJson {
  const json = candidate.toJSON();
  return {
    candidate: json.candidate ?? '',
    sdpMid: json.sdpMid ?? null,
    sdpMLineIndex: json.sdpMLineIndex ?? null,
    usernameFragment: json.usernameFragment ?? null,
  };
}

/** Caps the Opus sender at 40 kbps (h1-plan.md:200). */
export async function capBitrate(sender: RTCRtpSender): Promise<void> {
  try {
    const params = sender.getParameters();
    const encodings =
      params.encodings === undefined || params.encodings.length === 0 ? [{}] : params.encodings;
    params.encodings = encodings.map((e) => ({ ...e, maxBitrate: MAX_BITRATE_BPS }));
    await sender.setParameters(params);
  } catch {
    // Some browsers refuse before negotiation; the cap is a nicety, not a gate.
  }
}
